import { useState, useEffect, createContext, useContext, Component } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { ImportProvider } from "./contexts/ImportContext";

// Pages
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import AssistantPage from "./pages/AssistantPage";
import AnalyseCA from "./pages/AnalyseCA";
import Clients from "./pages/Clients";
import Factures from "./pages/Factures";
import Vigilance from "./pages/Vigilance";
import AdminUsers from "./pages/AdminUsers";
// Contrats Pages
import ContratsDashboard from "./pages/ContratsDashboard";
import ContratsPortefeuille from "./pages/ContratsPortefeuille";
import ContratsEcheances from "./pages/ContratsEcheances";
import ContratsFinancier from "./pages/ContratsFinancier";
import ContratsEvolution from "./pages/ContratsEvolution";
import ContratsAnnuaire from "./pages/ContratsAnnuaire";
import ContratDetail from "./pages/ContratDetail";
import ContratsParametres from "./pages/ContratsParametres";
// Rapprochement Bancaire V2 (Bridge API) - Legacy removed
// Achats / Factures Fournisseurs
import FacturesFournisseurs from "./pages/FacturesFournisseurs";
import AchatsOverview from "./pages/AchatsOverview";
import Fournisseurs from "./pages/Fournisseurs";
import ImportFournisseurs from "./pages/ImportFournisseurs";
import HistoriqueAchats from "./pages/HistoriqueAchats";
// Ventes - Import en masse et Historique
import ImportVentes from "./pages/ImportVentes";
import HistoriqueVentes from "./pages/HistoriqueVentes";
// Droit des Sociétés
import DroitSocietes from "./pages/DroitSocietes";
// Pilotage (Dashboard Dirigeant)
import Pilotage from "./pages/Pilotage";
// Ressources Humaines
import SalariesList from "./pages/rh/SalariesList";
import SalarieDetail from "./pages/rh/SalarieDetail";
import TrashPage from "./pages/rh/TrashPage";
import PayslipsOverviewPage from "./pages/rh/PayslipsOverviewPage";
import ContractsOverviewPage from "./pages/rh/ContractsOverviewPage";
import HRDashboardPage from "./pages/rh/HRDashboardPage";
import PayrollVariablesPage from "./pages/rh/PayrollVariablesPage";
// Parc Auto (Fleet)
import FleetDashboard from "./pages/fleet/FleetDashboard";
import VehiclesList from "./pages/fleet/VehiclesList";
import VehicleDetail from "./pages/fleet/VehicleDetail";
import FleetTrash from "./pages/fleet/FleetTrash";
import ContractsOverview from "./pages/fleet/ContractsOverview";
import ClaimsOverview from "./pages/fleet/ClaimsOverview";
// Comptabilité (Dossiers mensuels)
import Comptabilite from "./pages/Comptabilite";
import ComptabiliteParametres from "./pages/ComptabiliteParametres";
import ComptabiliteDossier from "./pages/ComptabiliteDossier";

// Contrats & Engagements (Dépenses récurrentes)
import EngagementsDashboard from "./pages/engagements/EngagementsDashboard";
import ContratsList from "./pages/engagements/ContratsList";
import ImportContrat from "./pages/engagements/ImportContrat";
import EngagementContratDetail from "./pages/engagements/ContratDetail";
import EngagementsParametres from "./pages/engagements/EngagementsParametres";
import EngagementsCalendrier from "./pages/engagements/EngagementsCalendrier";
import EngagementsResiliations from "./pages/engagements/EngagementsResiliations";

// Task Management (Microsoft To-Do like)
import TasksLayout from "./pages/TasksLayout";
import TasksPage from "./pages/tasks/TasksPage";
import TasksDashboard from "./pages/tasks/TasksDashboard";
import TasksKanban from "./pages/tasks/TasksKanban";

// Rapprochement Bancaire v2 (Bridge API)
import {
  RapprochementDashboard,
  RapprochementTransactions,
  RapprochementPending,
  RapprochementHistory,
  RapprochementRemises,
  RapprochementSettings,
  RapprochementBankStatements
} from "./pages/rapprochement-v2";

// Components
import Sidebar, { useIsMobile } from "./components/Sidebar";
import ImportModal from "./components/ImportModal";
import ImportPDFModal from "./components/ImportPDFModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global Error Boundary
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Ignore abort errors from PDF loading
    if (error?.message?.includes('aborted') || error?.name === 'AbortError') {
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Ignore abort errors
    if (error?.message?.includes('aborted') || error?.name === 'AbortError') {
      return;
    }
    console.error("App Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Une erreur s'est produite</h1>
            <p className="text-gray-600 mb-4">Veuillez rafraîchir la page</p>
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Rafraîchir
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Main Layout
import ImportProgressBar from "./components/ImportProgressBar";

const MainLayout = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50/50 flex flex-col">
      <div className="flex flex-1">
        <ImportProgressBar />
        <Sidebar />
        <main className={`flex-1 overflow-auto transition-all ${
          isMobile 
            ? 'pt-14 px-4 pb-6' 
            : 'ml-[220px] p-6'
        }`}>
          {children}
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ImportProvider>
          <BrowserRouter>
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/assistant" element={
              <ProtectedRoute>
                <MainLayout>
                  <AssistantPage />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/" element={
              <ProtectedRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/analyse" element={
              <ProtectedRoute>
                <MainLayout>
                  <AnalyseCA />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/clients" element={
              <ProtectedRoute>
                <MainLayout>
                  <Clients />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/factures" element={
              <ProtectedRoute>
                <MainLayout>
                  <Factures />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/vigilance" element={
              <ProtectedRoute>
                <MainLayout>
                  <Vigilance />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/import-ventes" element={
              <ProtectedRoute>
                <MainLayout>
                  <ImportVentes />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/historique-ventes" element={
              <ProtectedRoute>
                <MainLayout>
                  <HistoriqueVentes />
                </MainLayout>
              </ProtectedRoute>
            } />
            {/* Pilotage - Dashboard Dirigeant */}
            <Route path="/pilotage" element={
              <ProtectedRoute>
                <MainLayout>
                  <Pilotage />
                </MainLayout>
              </ProtectedRoute>
            } />
            {/* Achats / Factures Fournisseurs */}
            <Route path="/achats" element={
              <ProtectedRoute>
                <MainLayout>
                  <AchatsOverview />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/factures-fournisseurs" element={
              <ProtectedRoute>
                <MainLayout>
                  <FacturesFournisseurs />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/import-fournisseurs" element={
              <ProtectedRoute>
                <MainLayout>
                  <ImportFournisseurs />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fournisseurs" element={
              <ProtectedRoute>
                <MainLayout>
                  <Fournisseurs />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/historique-achats" element={
              <ProtectedRoute>
                <MainLayout>
                  <HistoriqueAchats />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute adminOnly>
                <MainLayout>
                  <AdminUsers />
                </MainLayout>
              </ProtectedRoute>
            } />
            {/* Contrats Routes */}
            <Route path="/contrats" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsDashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contrats/portefeuille" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsPortefeuille />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contrats/echeances" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsEcheances />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contrats/financier" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsFinancier />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contrats/evolution" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsEvolution />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contrats/annuaire" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsAnnuaire />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contrats/parametres" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsParametres />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/contrats/:contractId" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratDetail />
                </MainLayout>
              </ProtectedRoute>
            } />
            {/* Rapprochement Bancaire V2 (Bridge API) Routes */}
            <Route path="/rapprochement-v2" element={
              <ProtectedRoute>
                <MainLayout>
                  <RapprochementDashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rapprochement-v2/transactions" element={
              <ProtectedRoute>
                <MainLayout>
                  <RapprochementTransactions />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rapprochement-v2/pending" element={
              <ProtectedRoute>
                <MainLayout>
                  <RapprochementPending />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rapprochement-v2/settings" element={
              <ProtectedRoute>
                <MainLayout>
                  <RapprochementSettings />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rapprochement-v2/history" element={
              <ProtectedRoute>
                <MainLayout>
                  <RapprochementHistory />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rapprochement-v2/remises" element={
              <ProtectedRoute>
                <MainLayout>
                  <RapprochementRemises />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rapprochement-v2/bank-statements" element={
              <ProtectedRoute>
                <MainLayout>
                  <RapprochementBankStatements />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            {/* Droit des Sociétés Routes */}
            <Route path="/droit-societes" element={
              <ProtectedRoute>
                <MainLayout>
                  <DroitSocietes />
                </MainLayout>
              </ProtectedRoute>
            } />
            {/* Ressources Humaines Routes */}
            <Route path="/rh/salaries" element={
              <ProtectedRoute>
                <MainLayout>
                  <SalariesList />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rh/salaries/:id" element={
              <ProtectedRoute>
                <MainLayout>
                  <SalarieDetail />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rh/corbeille" element={
              <ProtectedRoute>
                <MainLayout>
                  <TrashPage />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rh/fiches-paie" element={
              <ProtectedRoute>
                <MainLayout>
                  <PayslipsOverviewPage />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rh/variables-paie" element={
              <ProtectedRoute>
                <MainLayout>
                  <PayrollVariablesPage />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rh/contrats" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContractsOverviewPage />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/rh/dashboard" element={
              <ProtectedRoute>
                <MainLayout>
                  <HRDashboardPage />
                </MainLayout>
              </ProtectedRoute>
            } />
            {/* Parc Auto (Fleet) Routes */}
            <Route path="/fleet" element={
              <ProtectedRoute>
                <MainLayout>
                  <FleetDashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fleet/dashboard" element={
              <ProtectedRoute>
                <MainLayout>
                  <FleetDashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fleet/vehicles" element={
              <ProtectedRoute>
                <MainLayout>
                  <VehiclesList />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fleet/vehicles/:id" element={
              <ProtectedRoute>
                <MainLayout>
                  <VehicleDetail />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fleet/trash" element={
              <ProtectedRoute>
                <MainLayout>
                  <FleetTrash />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fleet/contracts" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContractsOverview />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fleet/claims" element={
              <ProtectedRoute>
                <MainLayout>
                  <ClaimsOverview />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            {/* Comptabilité Routes */}
            <Route path="/comptabilite" element={
              <ProtectedRoute>
                <MainLayout>
                  <Comptabilite />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/comptabilite/parametres" element={
              <ProtectedRoute>
                <MainLayout>
                  <ComptabiliteParametres />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/comptabilite/dossier/:folderId" element={
              <ProtectedRoute>
                <MainLayout>
                  <ComptabiliteDossier />
                </MainLayout>
              </ProtectedRoute>
            } />

            {/* Contrats & Engagements Routes */}
            <Route path="/engagements" element={
              <ProtectedRoute>
                <MainLayout>
                  <EngagementsDashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/engagements/contrats" element={
              <ProtectedRoute>
                <MainLayout>
                  <ContratsList />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/engagements/import" element={
              <ProtectedRoute>
                <MainLayout>
                  <ImportContrat />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/engagements/contrats/:id" element={
              <ProtectedRoute>
                <MainLayout>
                  <EngagementContratDetail />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/engagements/calendrier" element={
              <ProtectedRoute>
                <MainLayout>
                  <EngagementsCalendrier />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/engagements/resiliations" element={
              <ProtectedRoute>
                <MainLayout>
                  <EngagementsResiliations />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/engagements/parametres" element={
              <ProtectedRoute>
                <MainLayout>
                  <EngagementsParametres />
                </MainLayout>
              </ProtectedRoute>
            } />

            {/* Task Management Routes */}
            <Route path="/tasks" element={
              <ProtectedRoute>
                <MainLayout>
                  <TasksLayout />
                </MainLayout>
              </ProtectedRoute>
            }>
              <Route index element={<TasksPage viewType="all" />} />
              <Route path="my-day" element={<TasksPage viewType="my-day" />} />
              <Route path="important" element={<TasksPage viewType="important" />} />
              <Route path="planned" element={<TasksPage viewType="planned" />} />
              <Route path="assigned" element={<TasksPage viewType="assigned" />} />
              <Route path="mentions" element={<TasksPage viewType="mentions" />} />
              <Route path="search" element={<TasksPage viewType="search" />} />
              <Route path="list/:listId" element={<TasksPage viewType="list" />} />
              <Route path="list/:listId/kanban" element={<TasksKanban />} />
              <Route path="kanban" element={<TasksKanban />} />
              <Route path="dashboard" element={<TasksDashboard />} />
            </Route>
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
        </ImportProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
